import { useImageEffectSettings } from "@saas/shared/hooks/use-image-effect-settings";
import type { ImageResolution } from "@saas/ai-image-generator/types";
import { TRANSFORMATION_PROMPTS } from "../constants/transformation";
import { useSession } from "@saas/auth/hooks/use-session";
import { useCredits } from "@saas/credits/hooks/use-credits";
import { usePurchases } from "@saas/payments/hooks/purchases";
import { useRouter } from "@shared/hooks/router";
import {
	PENDING_GENERATOR_STATE_KEY,
	PENDING_TRANSFORMATION_STATE_KEY,
	clearBlobPendingState,
	readBlobPendingState,
	saveBlobPendingState,
} from "@shared/lib/pending-state";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import type {
	TransformationConfig,
	TransformationType,
} from "../types/transformation";

// Re-export the type for backward compatibility
export type { TransformationType } from "../types/transformation";

const TRANSFORMATION_CONFIGS: Record<TransformationType, TransformationConfig> =
	{
		pixar: {
			credits: {
				required: 3,
				type: "IMAGE_GENERATION",
			},
		},
		anime: {
			credits: {
				required: 4,
				type: "IMAGE_GENERATION",
			},
		},
		age: {
			credits: {
				required: 4,
				type: "IMAGE_GENERATION",
			},
		},
		cartoon: {
			credits: {
				required: 4,
				type: "IMAGE_GENERATION",
			},
		},
	};

export function useImageTransformation(type: TransformationType) {
	const { user } = useSession();
	const router = useRouter();
	const translationKey =
		type === "pixar"
			? "imageToPixar"
			: type === "anime"
				? "imageToAnime"
				: type === "cartoon"
					? "imageToCartoon"
					: "ageFilter";
	const t = useTranslations(`${translationKey}.client`);
	const {
		activePlan,
		canRemoveWatermark,
		loading: purchasesLoading,
	} = usePurchases();
	const { getRemainingCredits, hasUnlimitedCredits } = useCredits();

	const config = TRANSFORMATION_CONFIGS[type];
	const imageSettings = useImageEffectSettings(type === "pixar" ? "v3.0" : "nano-banana-2-1");
	const { resolution, setResolution } = imageSettings;

	// Track all created Object URLs for proper cleanup
	const objectUrlsRef = useRef<Set<string>>(new Set());
	const hasRestoredStateRef = useRef(false); // Track if state has been restored

	// State management
	const [imageFile, setImageFile] = useState<File | null>(null);
	const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
	const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(
		null,
	);
	const [originalGeneratedImageUrl, setOriginalGeneratedImageUrl] = useState<
		string | null
	>(null);
	// Default aspect ratio for all transformation types is "1:1"
	const [aspectRatio, setAspectRatio] = useState("1:1");
	const [watermark, setWatermark] = useState(true);
	const [customPrompt, setCustomPrompt] = useState("");
	const [isFullPrompt, setIsFullPrompt] = useState(false);
	const [ageText, setAgeText] = useState(
		type === "age" ? "as a 25 year old" : "",
	); // Text-based age for age transformation
	const [restoredAgeValue, setRestoredAgeValue] = useState<number | null>(
		null,
	);
	const [showUpgradeModal, setShowUpgradeModal] = useState(false);

	// Whether the user can have watermark-free output (subscribed plan or credit pack purchase)
	const isFreeUser = !purchasesLoading && !canRemoveWatermark;

	// Automatically set watermark based on user plan
	useEffect(() => {
		if (!purchasesLoading) {
			setWatermark(isFreeUser);
		}
	}, [isFreeUser, purchasesLoading]);

	// Get credits information
	const usesRemaining = getRemainingCredits(config.credits.type);
	const hasUnlimitedCreditsForType = hasUnlimitedCredits(config.credits.type);

	// Convert image to Base64
	const convertImageToBase64 = (file: File): Promise<string> => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => {
				const base64String = reader.result as string;
				resolve(base64String);
			};
			reader.onerror = (error) => reject(error);
		});
	};

	// Helper function to track Object URLs
	const trackObjectUrl = useCallback((url: string) => {
		if (url && url.startsWith("blob:")) {
			objectUrlsRef.current.add(url);
		}
		return url;
	}, []);

	// Helper function to clean up a specific Object URL
	const cleanupObjectUrl = useCallback((url: string | null) => {
		if (url && url.startsWith("blob:")) {
			URL.revokeObjectURL(url);
			objectUrlsRef.current.delete(url);
		}
	}, []);

	// Handle image selection
	const handleImageSelected = useCallback(
		(file: File) => {
			// Clean up previous URL if exists
			cleanupObjectUrl(imagePreviewUrl);

			setImageFile(file);
			const url = trackObjectUrl(URL.createObjectURL(file));
			setImagePreviewUrl(url);
			setGeneratedImageUrl(null);
			setOriginalGeneratedImageUrl(null);
		},
		[imagePreviewUrl, trackObjectUrl, cleanupObjectUrl],
	);

	// Restore state from localStorage after login
	useEffect(() => {
		if (!user) return; // Only restore when user is logged in
		if (hasRestoredStateRef.current) return; // Don't restore twice
		if (imagePreviewUrl) return; // Don't restore if already has image

		void readBlobPendingState<{
			type?: string;
			imageBlob?: Blob;
			aspectRatio?: string;
			resolution?: ImageResolution;
			customPrompt?: string;
			isFullPrompt?: boolean;
			watermark?: boolean;
			isPublic?: boolean;
			ageText?: string;
			ageValue?: number;
		}>(PENDING_TRANSFORMATION_STATE_KEY, { maxAgeMs: 3600000 }).then(
			(state) => {
				if (!state) return;

				// Only restore state saved for the current transformation type;
				// leave it in place otherwise so the matching tool picks it up.
				if (state.type !== type) return;
				hasRestoredStateRef.current = true;

				// Restore image
				if (state.imageBlob) {
					handleImageSelected(
						new File(
							[state.imageBlob],
							`restored-image-${Date.now()}.jpg`,
							{ type: state.imageBlob.type },
						),
					);
				}

				// Restore settings
				if (state.resolution) setResolution(state.resolution);
				if (state.aspectRatio) {
					// For pixar type, convert match_input_image to original
					const restoredAspectRatio =
						type === "pixar" &&
						state.aspectRatio === "match_input_image"
							? "original"
							: state.aspectRatio;
					setAspectRatio(restoredAspectRatio);
				}
				if (state.customPrompt !== undefined) {
					setCustomPrompt(state.customPrompt);
					setIsFullPrompt(state.isFullPrompt ?? false);
				}
				if (type === "age" && state.ageText !== undefined) {
					setAgeText(state.ageText);
				}
				if (type === "age" && typeof state.ageValue === "number") {
					setRestoredAgeValue(state.ageValue);
				}

				// Clear the saved state after restoring
				void clearBlobPendingState(PENDING_TRANSFORMATION_STATE_KEY);
			},
		);
	}, [
		user,
		type,
		imagePreviewUrl,
		handleImageSelected,
		setAspectRatio,
		setCustomPrompt,
		setWatermark,
		setAgeText,
	]);

	// Handle generate transformation
	const handleGenerateTransformation = useCallback(async () => {
		if (!imageFile) {
			toast.error(t("errors.noImage"));
			return;
		}

		if (!user) {
			toast.error(t("errors.notAuthenticated"));
			return;
		}

		// For pixar, anime, cartoon, and age types, redirect to ai-edit-image
		if (
			type === "pixar" ||
			type === "anime" ||
			type === "age" ||
			type === "cartoon"
		) {
			try {
				// Convert image to base64
				const base64Image = await convertImageToBase64(imageFile);

				// System prompt based on transformation type
				let systemPrompt = "";
				let sourceType = "";

				if (type === "pixar") {
					systemPrompt = TRANSFORMATION_PROMPTS.pixar;
					sourceType = "photo-to-pixar";
				} else if (type === "anime") {
					systemPrompt = TRANSFORMATION_PROMPTS.anime;
					sourceType = "photo-to-anime";
				} else if (type === "cartoon") {
					systemPrompt = TRANSFORMATION_PROMPTS.cartoon;
					sourceType = "photo-to-cartoon";
				} else if (type === "age") {
					systemPrompt = `Transform the person in the image ${ageText}`;
					sourceType = "age-filter";
				}

				// Combine system prompt with custom prompt if provided
				const finalPrompt = customPrompt?.trim()
					? isFullPrompt
						? customPrompt.trim()
						: `${systemPrompt}, ${customPrompt.trim()}`
					: systemPrompt;

				// Map aspectRatio - "original" stays as is, will be mapped to "match_input_image" in generator
				const mappedAspectRatio = aspectRatio;

				// Prepare generator state for ai-edit-image
				const generatorState = {
					timestamp: Date.now(),
					activeTab: "image-to-image" as const,
					prompt: finalPrompt,
					inputImages: [base64Image],
					aspectRatio: mappedAspectRatio || "1:1",
					resolution,
					selectedModel: type === "pixar" ? "v3.0" : "nano-banana-2-1",
					watermark,
					isPublic: false,
					source: sourceType as any,
				};

				console.log(`[${sourceType}] Saving generator state:`, {
					aspectRatio,
					finalAspectRatio: generatorState.aspectRatio,
				});

				// generatorState.inputImages 是 base64，塞 localStorage 会撞 5MB 配额
				if (typeof window !== "undefined") {
					await saveBlobPendingState(
						PENDING_GENERATOR_STATE_KEY,
						generatorState,
					);
				}

				// Navigate to ai-edit-image
				const aspectRatioParam = encodeURIComponent(
					generatorState.aspectRatio,
				);
				const modelParam = encodeURIComponent(
					generatorState.selectedModel,
				);
				const sourceParam = encodeURIComponent(sourceType);
				router.push(
					`/ai-edit-image?model=${modelParam}&aspect_ratio=${aspectRatioParam}&source=${sourceParam}`,
				);
			} catch (error) {
				console.error("Error preparing generator state:", error);
				toast.error(t("errors.generationFailed"));
			}
			return;
		}

		// All transformation types redirect to ai-edit-image
		// This code should never be reached
		console.warn("Unexpected transformation type that doesn't redirect");
	}, [
		imageFile,
		user,
		t,
		type,
		router,
		aspectRatio,
		resolution,
		customPrompt,
		isFullPrompt,
		watermark,
		convertImageToBase64,
		ageText,
	]);

	// Clear generated image
	const clearGeneratedImage = useCallback(() => {
		// Clean up Object URLs if they exist
		cleanupObjectUrl(imagePreviewUrl);
		cleanupObjectUrl(generatedImageUrl);

		setImageFile(null);
		setImagePreviewUrl(null);
		setGeneratedImageUrl(null);
		setOriginalGeneratedImageUrl(null);
	}, [imagePreviewUrl, generatedImageUrl, cleanupObjectUrl]);

	// Clean up on unmount - comprehensive cleanup
	useEffect(() => {
		return () => {
			// Clean up all tracked Object URLs when component unmounts
			objectUrlsRef.current.forEach((url) => {
				URL.revokeObjectURL(url);
			});
			objectUrlsRef.current.clear();
		};
	}, []);

	return {
		...imageSettings,
		// State
		imagePreviewUrl,
		generatedImageUrl,
		originalGeneratedImageUrl,
		watermark,
		aspectRatio,
		customPrompt,
		isFullPrompt,
		usesRemaining,
		hasUnlimitedCredits: hasUnlimitedCreditsForType,
		isPending: false, // All types redirect, so no pending state
		showUpgradeModal,
		activePlan,

		// Actions
		handleImageSelected,
		handleGenerateTransformation,
		clearGeneratedImage,
		setWatermark,
		setAspectRatio,
		setCustomPrompt,
		setIsFullPrompt,
		setAgeText,
		setShowUpgradeModal,
		ageText,
		restoredAgeValue,
	};
}
